import { Injectable } from '@angular/core';
import { LotItem, InvoiceLotExtended, DeliveryNoteLotExtended } from '../models/inventory/lot-item';
import * as _ from 'lodash';
import ObjectID from 'bson-objectid';
import {
  ConsignmentBatchCommand,
  InvoiceLotAction,
  Shipment,
  DeliveryNoteLotAction,
  DeliveryNoteBatchCommand,
} from '../models/shipment/shipment';
import { InventoryLotProxyService } from '../proxies/inventory/inventory-lot.proxy.service';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { MessageService } from 'primeng/api';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';

export interface LotAdditionParams {
  itemCode: string;
  numberOfLots: number;
  providerLotId?: string;
  unitsPerLot?: number;
  startProviderLabelId?: number;
}

@Injectable({
  providedIn: 'root',
})
export class LotsService {
  invoiceLots: Record<string, InvoiceLotExtended> = {};
  editingInvoiceLot: InvoiceLotExtended | null = null;
  deliveryNoteLots: Record<string, DeliveryNoteLotExtended> = {};
  editingDeliveryNoteLot: DeliveryNoteLotExtended | null = null;
  lotItemsForConsignment: LotItem[] = [];
  private shipment: Shipment | null = null;
  private isLoading = 0;
  public isConsignmentMode = false;
  public isDeliveryNoteMode = false;

  public get loading(): boolean {
    return this.isLoading > 0;
  }

  constructor(
    private readonly inventoryLotProxyService: InventoryLotProxyService,
    private readonly messageService: MessageService,
  ) {}

  /**
   * Creates a composite key for invoice-purchase order combination
   */
  private getCompositeKey(invoiceId: string, purchaseOrderId: string): string {
    return `${invoiceId}-${purchaseOrderId}`;
  }

  /**
   * Creates a composite key for delivery note-work order combination
   */
  private getCompositeKeyForDeliveryNote(deliveryNoteId: string, workOrderId: string): string {
    return `${deliveryNoteId}-${workOrderId}`;
  }

  //# region Initialization

  public initializeForShipment(shipment: Shipment) {
    this.clear();
    this.shipment = shipment;
    this.isConsignmentMode = true;
    this.refreshShipmentLots();
  }

  public resetState() {
    this.invoiceLots = {};
    this.editingInvoiceLot = null;
    this.deliveryNoteLots = {};
    this.editingDeliveryNoteLot = null;
    this.lotItemsForConsignment = [];
    this.shipment = null;
    this.isConsignmentMode = false;
    this.isDeliveryNoteMode = false;
  }

  public addInvoiceLots(invoiceLots: InvoiceLotExtended[]) {
    _.forEach(invoiceLots, (invoiceLot) => {
      if (invoiceLot.invoice?.id && invoiceLot.purchaseOrder?.id) {
        const key = this.getCompositeKey(invoiceLot.invoice.id, invoiceLot.purchaseOrder.id);
        this.invoiceLots[key] = invoiceLot;
      }
    });
  }

  public addDeliveryNoteLots(deliveryNoteLots: DeliveryNoteLotExtended[]) {
    _.forEach(deliveryNoteLots, (deliveryNoteLot) => {
      if (deliveryNoteLot.deliveryNote?.id && deliveryNoteLot.workOrder?.id) {
        const key = this.getCompositeKeyForDeliveryNote(deliveryNoteLot.deliveryNote.id, deliveryNoteLot.workOrder.id);
        this.deliveryNoteLots[key] = deliveryNoteLot;
      }
    });
  }

  //# endregion

  //# region getters
  public refreshShipmentLots() {
    if (_.isNil(this.shipment) || _.isEmpty(this.shipment.deliveryNoteReferences)) {
      return;
    }
    this.isLoading++;
    const requests = this.shipment.deliveryNoteReferences!.map((dnId) => this.inventoryLotProxyService.getLotsByDeliveryNoteId(dnId));
    forkJoin(requests)
      .pipe(
        map((results) => {
          this.isLoading--;
          this.lotItemsForConsignment = results.flatMap((result) => (result.success && result.data ? result.data : []));
        }),
        catchError((error) => {
          this.isLoading--;
          console.error(error);
          return of(undefined);
        }),
      )
      .subscribe();
  }

  public getInvoiceLots(invoiceId: string): LotItem[] {
    const keysWithInvoice = Object.keys(this.invoiceLots).filter((key) => key.startsWith(`${invoiceId}-`));
    const allLots: LotItem[] = [];
    keysWithInvoice.forEach((key) => {
      allLots.push(...this.invoiceLots[key].savedLots, ...this.invoiceLots[key].localLots);
    });
    return allLots;
  }

  public getLocalLots(invoiceId: string, purchaseOrderId: string): LotItem[] {
    const key = this.getCompositeKey(invoiceId, purchaseOrderId);
    return this.invoiceLots[key]?.localLots ?? [];
  }

  /**
   * Gets all lots for all purchase orders in an invoice
   */
  public getAllInvoiceLots(invoiceId: string): LotItem[] {
    const allLots: LotItem[] = [];
    Object.keys(this.invoiceLots).forEach((key) => {
      if (key.startsWith(`${invoiceId}-`)) {
        const invoiceLot = this.invoiceLots[key];
        allLots.push(...invoiceLot.savedLots, ...invoiceLot.localLots);
      }
    });
    return allLots;
  }

  public getDeliveryNoteLots(deliveryNoteId: string): LotItem[] {
    const keysWithDeliveryNote = Object.keys(this.deliveryNoteLots).filter((key) => key.startsWith(`${deliveryNoteId}-`));
    const allLots: LotItem[] = [];
    keysWithDeliveryNote.forEach((key) => {
      allLots.push(...this.deliveryNoteLots[key].savedLots, ...this.deliveryNoteLots[key].localLots);
    });
    return allLots;
  }

  public getLocalDeliveryNoteLots(deliveryNoteId: string, workOrderId: string): LotItem[] {
    const key = this.getCompositeKeyForDeliveryNote(deliveryNoteId, workOrderId);
    return this.deliveryNoteLots[key]?.localLots ?? [];
  }

  public getAllDeliveryNoteLots(deliveryNoteId: string): LotItem[] {
    const allLots: LotItem[] = [];
    Object.keys(this.deliveryNoteLots).forEach((key) => {
      if (key.startsWith(`${deliveryNoteId}-`)) {
        const deliveryNoteLot = this.deliveryNoteLots[key];
        allLots.push(...deliveryNoteLot.savedLots, ...deliveryNoteLot.localLots);
      }
    });
    return allLots;
  }

  //# endregion

  //# ragion actions
  public setEditingInvoiceLot(invoiceId: string, purchaseOrderId: string) {
    const key = this.getCompositeKey(invoiceId, purchaseOrderId);
    if (this.invoiceLots[key]) {
      this.editingInvoiceLot = _.cloneDeep(this.invoiceLots[key]);
    }
  }

  public setEditingDeliveryNoteLot(deliveryNoteId: string, workOrderId: string) {
    const key = this.getCompositeKeyForDeliveryNote(deliveryNoteId, workOrderId);
    if (this.deliveryNoteLots[key]) {
      this.editingDeliveryNoteLot = _.cloneDeep(this.deliveryNoteLots[key]);
    }
  }

  public cancelEditing() {
    this.editingInvoiceLot = null;
    this.editingDeliveryNoteLot = null;
  }

  public clear() {
    this.invoiceLots = {};
    this.editingInvoiceLot = null;
    this.deliveryNoteLots = {};
    this.editingDeliveryNoteLot = null;
  }

  public saveEditing() {
    if (this.editingInvoiceLot?.invoice?.id && this.editingInvoiceLot?.purchaseOrder?.id) {
      const key = this.getCompositeKey(this.editingInvoiceLot.invoice.id, this.editingInvoiceLot.purchaseOrder.id);
      this.invoiceLots[key] = this.editingInvoiceLot;
      this.editingInvoiceLot = null;
    }
    if (this.editingDeliveryNoteLot?.deliveryNote?.id && this.editingDeliveryNoteLot?.workOrder?.id) {
      const key = this.getCompositeKeyForDeliveryNote(
        this.editingDeliveryNoteLot.deliveryNote.id,
        this.editingDeliveryNoteLot.workOrder.id,
      );
      this.deliveryNoteLots[key] = this.editingDeliveryNoteLot;
      this.editingDeliveryNoteLot = null;
    }
  }
  //# endregion

  //# region CRUD
  public addLots(lotAdditionParams: LotAdditionParams[]) {
    if (this.editingInvoiceLot) {
      const newLots = this.createInventoryLots(lotAdditionParams);
      this.editingInvoiceLot.localLots = _.concat(this.editingInvoiceLot.localLots, newLots);
      this.saveEditing();
    } else if (this.editingDeliveryNoteLot) {
      const newLots = this.createDeliveryNoteLots(lotAdditionParams);
      this.editingDeliveryNoteLot.localLots = _.concat(this.editingDeliveryNoteLot.localLots, newLots);
      this.saveEditing();
    }
  }

  public batchAddLots(newLots: LotItem[]) {
    if (this.isConsignmentMode) {
      this.lotItemsForConsignment = _.concat(this.lotItemsForConsignment, newLots);
    } else if (this.editingInvoiceLot) {
      this.editingInvoiceLot.localLots = _.concat(this.editingInvoiceLot.localLots, newLots);
      this.saveEditing();
    }
  }

  public deleteLotItemConsignment(lotItemNumber: string): Observable<void> {
    const lotItem = this.lotItemsForConsignment.find((lot) => lot.number === lotItemNumber);
    if (_.isNil(lotItem?.id)) {
      //local lot, just remove it from the array
      this.lotItemsForConsignment = this.lotItemsForConsignment.filter((lot) => lot.number !== lotItemNumber);
      GlobalMessageUtil.success('Lot Items', 'Lot item deleted successfully.');
      return of(undefined);
    }
    return this.inventoryLotProxyService.deleteLotItem(lotItem.id).pipe(
      map((result) => {
        if (result.success) {
          this.lotItemsForConsignment = this.lotItemsForConsignment.filter((lot) => lot.id !== lotItem.id);
          GlobalMessageUtil.success('Lot Items', 'Lot item deleted successfully.');
        }
      }),
      catchError((error) => {
        console.error(error);
        return of(undefined);
      }),
    );
  }

  public removeInvoice(invoiceId: string): void {
    Object.keys(this.invoiceLots)
      .filter((key) => key.startsWith(`${invoiceId}-`))
      .forEach((key) => delete this.invoiceLots[key]);
  }

  public deleteLots(invoiceId: string, purchaseOrderId: string, lotIds?: string[]): Observable<boolean> {
    const key = this.getCompositeKey(invoiceId, purchaseOrderId);

    if (_.isNil(lotIds)) {
      //remove whole invoice-PO combination
      delete this.invoiceLots[key];
      return of(true);
    } else {
      const invoiceLot = this.invoiceLots[key];
      if (_.isNil(invoiceLot?.invoice?.id) || _.isNil(invoiceLot?.purchaseOrder?.id)) {
        return of(false);
      }
      const invoiceLotAction: InvoiceLotAction = {
        invoiceReference: invoiceLot.invoice.id,
        purchaseOrderReference: invoiceLot.purchaseOrder.id,
        newLots: [],
        lotIdsToDelete: invoiceLot.savedLots.filter((lot) => lotIds.includes(lot.number!)).map((lot) => lot.number!) ?? [],
      };
      if (invoiceLotAction.lotIdsToDelete.length === 0) {
        invoiceLot.localLots = invoiceLot.localLots.filter((lot) => !lotIds.includes(lot.number!));
        return of(true);
      }
      return this.inventoryLotProxyService.batchAddInventoryLots([invoiceLotAction]).pipe(
        map((result) => {
          if (result) {
            invoiceLot.localLots = invoiceLot.localLots.filter((lot) => !lotIds.includes(lot.number!));
            invoiceLot.savedLots = invoiceLot.savedLots.filter((lot) => !lotIds.includes(lot.number!));
            return true;
          }
          return false;
        }),
        catchError((error) => {
          console.error(error);
          return of(false);
        }),
      );
    }
  }
  //

  public validateQuantityAndUnitsPerLot(lotAdditionParam: LotAdditionParams): boolean {
    if (
      !this.editingInvoiceLot ||
      !this.editingInvoiceLot.invoice.id ||
      !this.editingInvoiceLot.invoice.items ||
      !this.editingInvoiceLot.purchaseOrder?.id
    ) {
      return false;
    }

    const consumedInvoiceItemQuantity = _.sumBy(this.getInvoiceLots(this.editingInvoiceLot.invoice.id), (lot) =>
      lot.itemCode === lotAdditionParam.itemCode ? lot.grossQuantity : 0,
    );
    const lotAdditionQuantity = lotAdditionParam.numberOfLots * (lotAdditionParam.unitsPerLot ?? 0);

    const invoiceItem = this.editingInvoiceLot.invoice.items.find((item) => item.itemInPurchaseOrderId === lotAdditionParam.itemCode);
    if (!invoiceItem) {
      return false;
    }
    return consumedInvoiceItemQuantity + lotAdditionQuantity <= invoiceItem.quantity;
  }

  //# region helpers
  private createInventoryLots(params: LotAdditionParams[]): LotItem[] {
    const newLots: LotItem[] = [];
    _.forEach(params, (param) => {
      if (_.isNil(this.editingInvoiceLot) || _.isNil(this.editingInvoiceLot.purchaseOrder?.purchaseOrderNumber)) {
        throw new Error('Editing invoice lot is not set or purchase order number is not set');
      }
      const item = _.find(this.editingInvoiceLot.purchaseOrder.items, (item) => item.itemCode === param.itemCode);
      if (!item) {
        throw new Error('Item not found in purchase order');
      }
      const lastLost = _.last(this.editingInvoiceLot.localLots);
      const startProviderLabelId = param.startProviderLabelId ?? null;
      // use the last lot to get the next lot id, lotId = Local-1 and so on for local lots
      let lotIdNumber = lastLost && lastLost.number ? parseInt(lastLost.number.split('-')[1]) + 1 : 1;
      for (let i = 0; i < param.numberOfLots; i++) {
        if (i > 0) {
          lotIdNumber++;
        }

        const newLot: LotItem = {
          id: new ObjectID().toHexString(),
          number: `Local-${lotIdNumber}`,
          createdBy: 'jon-test',
          itemCode: param.itemCode,
          consumptionEntries: [],
          netQuantity: param.unitsPerLot ?? 0,
          originalGrossQuantity: param.unitsPerLot ?? 0,
          originalNetQuantity: param.unitsPerLot ?? 0,
          grossQuantity: param.unitsPerLot ?? 0,
          invoiceReference: this.editingInvoiceLot.invoice?.id ?? '',
          purchaseOrderReference: this.editingInvoiceLot.purchaseOrder.id ?? undefined,
          originReference: this.editingInvoiceLot.purchaseOrder.providerReference ?? undefined,
          providerReference: this.editingInvoiceLot.purchaseOrder.providerReference,
          providerLabelNumber: startProviderLabelId ? `${startProviderLabelId + i}` : '',
          inbound: true,
          notes: [],
          providerLotId: param.providerLotId!,
          local: true,
          isActive: true,
          type: this.getLotItemTypeFromPurchaseOrder(this.editingInvoiceLot.purchaseOrder.type),
          packagingType: 0,
        };
        newLots.push(newLot);
      }
    });

    return newLots;
  }

  /**
   * Maps PurchaseOrderType to LotItemType.
   * PurchaseOrderType.Chemicals (2) → LotItemType.Chemical (2)
   * All others → LotItemType.LotItem (0)
   */
  public getLotItemTypeFromPurchaseOrder(poType: number): number {
    return poType === 2 ? 2 : 0;
  }

  private createDeliveryNoteLots(params: LotAdditionParams[]): LotItem[] {
    const newLots: LotItem[] = [];
    _.forEach(params, (param) => {
      if (_.isNil(this.editingDeliveryNoteLot) || _.isNil(this.editingDeliveryNoteLot.workOrder?.id)) {
        throw new Error('Editing delivery note lot is not set or work order is not set');
      }
      const fabric = _.find(this.editingDeliveryNoteLot.workOrder.fabrics, (fabric) => fabric.fabricCode === param.itemCode);
      if (!fabric) {
        throw new Error('Fabric not found in work order');
      }
      const lastLot = _.last(this.editingDeliveryNoteLot.localLots);
      const startProviderLabelId = param.startProviderLabelId ?? null;
      let lotIdNumber = lastLot && lastLot.number ? parseInt(lastLot.number.split('-')[1]) + 1 : 1;

      for (let i = 0; i < param.numberOfLots; i++) {
        if (i > 0) {
          lotIdNumber++;
        }

        const newLot: LotItem = {
          id: new ObjectID().toHexString(),
          number: `Local-${lotIdNumber}`,
          createdBy: 'system',
          itemCode: param.itemCode,
          consumptionEntries: [],
          netQuantity: param.unitsPerLot ?? 0,
          originalGrossQuantity: param.unitsPerLot ?? 0,
          originalNetQuantity: param.unitsPerLot ?? 0,
          grossQuantity: param.unitsPerLot ?? 0,
          deliveryNoteReference: this.editingDeliveryNoteLot.deliveryNote?.id ?? '',
          workOrderReference: this.editingDeliveryNoteLot.workOrder.id ?? undefined,
          clientReference: this.editingDeliveryNoteLot.workOrder.customerReference ?? undefined,
          providerLabelNumber: startProviderLabelId ? `${startProviderLabelId + i}` : '',
          inbound: false, // Delivery notes are outbound
          notes: [],
          providerLotId: param.providerLotId!,
          local: true,
          isActive: true,
          type: 0, // Delivery note lots are always LotItem type
          packagingType: 0,
        };
        newLots.push(newLot);
      }
    });

    return newLots;
  }

  deleteLocalLot(invoiceId: string, purchaseOrderId: string, lotId: string) {
    const key = this.getCompositeKey(invoiceId, purchaseOrderId);
    const invoiceLot = this.invoiceLots[key];
    if (invoiceLot) {
      invoiceLot.localLots = invoiceLot.localLots.filter((lot) => lot.id !== lotId);
    }
  }

  deleteSavedLot(invoiceId: string, purchaseOrderId: string, lotId: string) {
    const key = this.getCompositeKey(invoiceId, purchaseOrderId);
    const invoiceLot = this.invoiceLots[key];
    if (invoiceLot) {
      // make http call to delete the lot
      invoiceLot.savedLots = invoiceLot.savedLots.filter((lot) => lot.id !== lotId);
    }
  }

  public disableSaveLotsButton(): boolean {
    const invoiceLotActions: InvoiceLotAction[] = Object.values(this.invoiceLots).map((invoiceLot) => {
      const allLots: LotItem[] = [
        ...invoiceLot.localLots.map((lot) => ({
          ...lot,
          id: undefined,
          number: undefined,
        })),
      ];
      const lotIdsToDelete: string[] = []; // IDs of lots to delete that are NOT local
      return {
        invoiceReference: invoiceLot.invoice.id ?? '',
        purchaseOrderReference: invoiceLot.purchaseOrder.id ?? '',
        newLots: allLots,
        lotIdsToDelete,
      };
    });

    return !invoiceLotActions.some((action) => action.newLots.length > 0 || action.lotIdsToDelete.length > 0);
  }

  //# endregion

  //# region Actions

  public saveLots(shipmentReference?: string): Observable<boolean> {
    const invoiceLotActions: InvoiceLotAction[] = Object.values(this.invoiceLots).map((invoiceLot) => {
      const allLots: LotItem[] = [
        ...invoiceLot.savedLots,
        ...invoiceLot.localLots.map((lot) => ({
          ...lot,
          id: undefined,
          number: undefined,
        })),
      ];
      const lotIdsToDelete: string[] = []; // IDs of lots to delete that are NOT local
      return {
        invoiceReference: invoiceLot.invoice.id ?? '',
        purchaseOrderReference: invoiceLot.purchaseOrder.id ?? '',
        newLots: allLots,
        lotIdsToDelete,
      };
    });

    this.lotItemsForConsignment = this.lotItemsForConsignment.map((lot) => {
      if (!lot.id) {
        return {
          ...lot,
          number: undefined,
        };
      }
      return lot;
    });

    const anythingToSave = this.isConsignmentMode
      ? this.lotItemsForConsignment.length > 0
      : invoiceLotActions.some((action) => action.newLots.length > 0 || action.lotIdsToDelete.length > 0);
    if (!anythingToSave) {
      GlobalMessageUtil.info('Lot Items', 'No changes to save.');
      return of(false);
    }

    if (this.isConsignmentMode) {
      if (_.isNil(shipmentReference)) {
        console.error('Shipment reference is required for consignment mode');
        return of(false);
      }
      const command: ConsignmentBatchCommand = {
        shipmentReference: shipmentReference,
        lotItems: this.lotItemsForConsignment,
      };
      return this.inventoryLotProxyService.batchUpsertLotsByShipment(command).pipe(
        map((result) => {
          if (result) {
            this.lotItemsForConsignment = result.data ?? [];
            return true;
          }
          return false;
        }),
        catchError(() => of(false)),
      );
    } else {
      if (invoiceLotActions.length > 0) {
        const invoiceLotActionsWithActions = invoiceLotActions.filter(
          (action) => action.newLots.length > 0 || action.lotIdsToDelete.length > 0,
        );
        return this.inventoryLotProxyService.batchAddInventoryLots(invoiceLotActionsWithActions).pipe(
          map((result) => {
            if (result) {
              if (result.data) {
                Object.values(this.invoiceLots).forEach((invoiceLot) => {
                  invoiceLot.localLots = [];
                  invoiceLot.savedLots = result.data!.filter((lot) => lot.invoiceReference === invoiceLot.invoice.id);
                });
              }

              return true;
            }
            return false;
          }),
          catchError(() => of(false)),
        );
      } else {
        return of(false);
      }
    }
  }

  public saveDeliveryNoteLots(shipmentReference: string): Observable<boolean> {
    if (!this.isDeliveryNoteMode) {
      return of(false);
    }

    const deliveryNoteLotActions: DeliveryNoteLotAction[] = Object.values(this.deliveryNoteLots).map((deliveryNoteLot) => {
      const allLots: LotItem[] = [
        ...deliveryNoteLot.savedLots,
        ...deliveryNoteLot.localLots.map((lot) => ({
          ...lot,
          id: undefined,
          number: undefined,
        })),
      ];
      const lotIdsToDelete: string[] = [];
      return {
        deliveryNoteReference: deliveryNoteLot.deliveryNote.id ?? '',
        workOrderReference: deliveryNoteLot.workOrder.id ?? '',
        newLots: allLots,
        lotIdsToDelete,
      };
    });

    const anythingToSave = deliveryNoteLotActions.some((action) => action.newLots.length > 0 || action.lotIdsToDelete.length > 0);
    if (!anythingToSave) {
      GlobalMessageUtil.info('Lot Items', 'No changes to save.');
      return of(false);
    }

    const command: DeliveryNoteBatchCommand = {
      shipmentReference: shipmentReference,
      deliveryNoteLotActions: deliveryNoteLotActions,
    };

    return this.inventoryLotProxyService.batchAddDeliveryNoteLots(command).pipe(
      map((result) => {
        if (result.success && result.data) {
          Object.values(this.deliveryNoteLots).forEach((deliveryNoteLot) => {
            deliveryNoteLot.localLots = [];
            deliveryNoteLot.savedLots = result.data!.filter((lot) => lot.deliveryNoteReference === deliveryNoteLot.deliveryNote.id);
          });
          return true;
        }
        return false;
      }),
      catchError(() => of(false)),
    );
  }

  public disableSaveDeliveryNoteLotsButton(): boolean {
    const deliveryNoteLotActions: DeliveryNoteLotAction[] = Object.values(this.deliveryNoteLots).map((deliveryNoteLot) => {
      const allLots: LotItem[] = [
        ...deliveryNoteLot.localLots.map((lot) => ({
          ...lot,
          id: undefined,
          number: undefined,
        })),
      ];
      const lotIdsToDelete: string[] = [];
      return {
        deliveryNoteReference: deliveryNoteLot.deliveryNote.id ?? '',
        workOrderReference: deliveryNoteLot.workOrder.id ?? '',
        newLots: allLots,
        lotIdsToDelete,
      };
    });

    return !deliveryNoteLotActions.some((action) => action.newLots.length > 0 || action.lotIdsToDelete.length > 0);
  }
}
